import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import AdminSidebar from './AdminSidebar';

import { API_BASE_URL } from '../../config';
import './AdminDashboard.css'; // Reuse common layout styles
import './AdminUsers.css'; // Reuse common table/pagination styles
import './AdminTests.css';

const AdminTests = () => {
    const { t } = useTranslation(['admin', 'common']);
    const [attempts, setAttempts] = useState([]);

    const [totalAttempts, setTotalAttempts] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const recordsPerPage = 20;

    useEffect(() => {
        fetchAttempts();
    }, [currentPage, searchTerm]);

    const fetchAttempts = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}get_all_test_attempts.php?page=${currentPage}&limit=${recordsPerPage}&search=${encodeURIComponent(searchTerm)}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('adminId')}`
                },
                credentials: 'include'
            });
            const data = await response.json();
            if (data.status === 'success') {
                setAttempts(data.attempts);
                setTotalAttempts(data.total_attempts);
            } else {
                setError(data.message);
            }
        } catch (err) {
            setError(t('admin:tests.errors.loadFailed', 'Failed to load test attempts'));
        } finally {

            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const totalPages = Math.ceil(totalAttempts / recordsPerPage);
    const indexOfFirstRecord = (currentPage - 1) * recordsPerPage;
    const indexOfLastRecord = Math.min(currentPage * recordsPerPage, totalAttempts);

    const paginate = (pageNumber) => {
        if (pageNumber >= 1 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
        }
    };

    const getScoreColor = (percentage) => {
        if (percentage >= 80) return 'score-high';
        if (percentage >= 50) return 'score-medium';
        return 'score-low';
    };

    if (loading && attempts.length === 0) return (
        <div className="admin-loading-screen">
            <div className="loader-inner">
                <div className="loader-orbit"></div>
                <div className="loader-text">{t('admin:tests.loading', 'Loading Test Records...')}</div>
            </div>
        </div>

    );

    return (
        <div className={`admin-layout${sidebarOpen ? '' : ' sidebar-collapsed'}`}>
            <AdminSidebar sidebarOpen={sidebarOpen} onToggle={() => setSidebarOpen(o => !o)} />

            <main className="admin-main">
                <header className="main-header">
                    <button
                        className="sidebar-toggle-btn"
                        onClick={() => setSidebarOpen(o => !o)}
                        aria-label="Toggle sidebar"
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '42px',
                            height: '42px',
                            fontSize: '1.4rem',
                            color: '#f8fafc',
                            background: 'rgba(59,130,246,0.15)',
                            border: '1px solid rgba(59,130,246,0.4)',
                            borderRadius: '10px',
                            cursor: 'pointer',
                            flexShrink: 0,
                            padding: 0,
                        }}
                    >
                        ☰
                    </button>
                    <div className="header-search">
                        <input
                            type="text"
                            placeholder={t('admin:tests.searchPlaceholder', 'Search by User, Subject or Chapter...')}
                            value={searchTerm}
                            onChange={handleSearch}
                        />

                        <span className="search-icon">🔍</span>
                    </div>
                    <div className="header-actions">
                        <div className="date-display">{new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
                    </div>
                </header>

                <section className="content-inner">
                    <div className="page-title-section">
                        <h1>{t('admin:tests.title', 'Test Analytics')}</h1>
                        <p>{t('admin:tests.subtitle', 'View all AI-generated test attempts and student performance across chapters.')}</p>
                    </div>


                    {error && <div className="error-alert">{error}</div>}

                    <div className="data-panel full-width">
                        <div className="panel-header">
                            <div className="panel-info">
                                <h2>{t('admin:tests.allAttempts', { count: totalAttempts, defaultValue: `All Attempts (${totalAttempts})` })}</h2>
                                {totalAttempts > 0 && (
                                    <p className="pagination-info">{t('admin:users.showing', { start: indexOfFirstRecord + 1, end: indexOfLastRecord, total: totalAttempts, defaultValue: `Showing ${indexOfFirstRecord + 1}-${indexOfLastRecord} of ${totalAttempts}` })}</p>
                                )}
                            </div>

                            <button className="export-btn" onClick={() => window.print()}>{t('admin:tests.printAnalytics', 'Print Analytics')}</button>

                        </div>

                        <div className="table-wrapper">
                            <table className="premium-table">
                                <thead>
                                    <tr>
                                        <th>{t('admin:tests.table.student', 'Student')}</th>
                                        <th>{t('admin:tests.table.academicContext', 'Academic Context')}</th>
                                        <th>{t('admin:tests.table.performance', 'Performance')}</th>
                                        <th>{t('admin:tests.table.stats', 'Stats')}</th>
                                        <th>{t('admin:tests.table.attemptedAt', 'Attempted At')}</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {attempts.map((attempt, index) => (
                                        <tr key={attempt.id || index}>
                                            <td>
                                                <div className="user-info-text">
                                                    <p className="user-name">{attempt.full_name}</p>
                                                    <p className="user-email">{attempt.email}</p>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="academic-details">
                                                    <p className="subject-tag">{attempt.subject_name}</p>
                                                    <p className="chapter-tag">{attempt.chapter_name || 'Full Subject'}</p>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="score-cell">
                                                    <span className={`score-badge ${getScoreColor(attempt.score_percentage)}`}>
                                                        {attempt.score_percentage}%
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="stats-info">
                                                    <p>{t('admin:tests.correct', 'Correct')}: <strong>{attempt.correct_answers}</strong></p>
                                                    <p>{t('admin:tests.total', 'Total')}: {attempt.total_questions}</p>
                                                </div>
                                            </td>

                                            <td>{new Date(attempt.submitted_at).toLocaleDateString()}</td>
                                        </tr>
                                    ))}
                                    {attempts.length === 0 && !loading && (
                                        <tr>
                                            <td colSpan="5" className="empty-state">{t('admin:tests.table.empty', 'No test attempts found')}</td>
                                        </tr>
                                    )}

                                </tbody>
                            </table>
                            {loading && attempts.length > 0 && (
                                <div className="table-loading-overlay">Updating...</div>
                            )}
                        </div>

                        {/* Pagination Controls */}
                        {totalPages > 1 && (
                            <div className="pagination-controls">
                                <button
                                    onClick={() => paginate(currentPage - 1)}
                                    disabled={currentPage === 1 || loading}
                                    className="page-btn prev"
                                >
                                    &laquo; {t('admin:pagination.previous', 'Previous')}
                                </button>

                                <div className="page-numbers">
                                    {[...Array(totalPages)].map((_, index) => {
                                        const pageNum = index + 1;
                                        if (
                                            pageNum === 1 ||
                                            pageNum === totalPages ||
                                            (pageNum >= currentPage - 2 && pageNum <= currentPage + 2)
                                        ) {
                                            return (
                                                <button
                                                    key={pageNum}
                                                    onClick={() => paginate(pageNum)}
                                                    disabled={loading}
                                                    className={`page-number ${currentPage === pageNum ? 'active' : ''}`}
                                                >
                                                    {pageNum}
                                                </button>
                                            );
                                        } else if (
                                            pageNum === currentPage - 3 ||
                                            pageNum === currentPage + 3
                                        ) {
                                            return <span key={pageNum} className="pagination-ellipsis">...</span>;
                                        }
                                        return null;
                                    })}
                                </div>
                                <button
                                    onClick={() => paginate(currentPage + 1)}
                                    disabled={currentPage === totalPages || loading}
                                    className="page-btn next"
                                >
                                    {t('admin:pagination.next', 'Next')} &raquo;
                                </button>

                            </div>
                        )}
                    </div>
                </section>
            </main>
        </div>
    );
};

export default AdminTests;
